import { FC } from "react";

import {
  ReportSectionFieldTypeEnums,
  ReportSectionsType,
  useCreateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { AddRecommendationsPanel } from "@components/AddRecommendationsPanel/AddRecommendationsPanel";
import { useCanAddRecommendation } from "@components/ReportField/capabilities";
import { useIsClinicalSurface } from "@components/ReportField/capabilities/flags";
import { notifyError } from "@components/notifications/notifications";
import { ClinicalReportAddRecommendationsPanel } from "@pages/ClinicalReportDetails/Components/ClinicalReportAddRecommendationsPanel";
import { getNewFieldRankOrder, getSortedFieldsByRankOrder } from "@pages/EvaluationDetails/Components/helper";
import { useReport } from "@providers/ReportProvider";

interface Props {
  section: ReportSectionsType;
}

// Evaluation renders the recommendations bank + custom-recommendation panel
// at the end of a recommendations section; Clinical has its own
// ClinicalReportAddRecommendationsPanel with Catalight-specific rendering.
export const ReportSectionRecommendationsPanel: FC<Props> = ({ section }) => {
  const { report, reportQuery } = useReport();
  const canAddRecommendation = useCanAddRecommendation(section);
  const isClinicalSurface = useIsClinicalSurface();
  const { mutateAsync: addSectionFieldMutate } = useCreateReportSectionFieldMutation({
    onSuccess: async (data) => {
      if (data?.createReportSectionField?.status) {
        await reportQuery.refetch();
      }
    },
    onError: (error) => {
      console.error("Error while adding section field:", error);
      notifyError("Error Adding Section Field");
    },
  });

  if (!canAddRecommendation) return null;

  const fields = getSortedFieldsByRankOrder(section);

  const handleAddContent = (fieldType: ReportSectionFieldTypeEnums, newRankOrder: number) =>
    addSectionFieldMutate({
      reportId: report.id,
      reportSectionId: section.id,
      fieldData: { title: "Enter Title", rankOrder: newRankOrder, fieldType },
    });

  const Panel = isClinicalSurface ? ClinicalReportAddRecommendationsPanel : AddRecommendationsPanel;

  return (
    <Panel
      sectionId={section.id}
      onAddContent={handleAddContent}
      newRankOrder={getNewFieldRankOrder(section)}
      displayNoContentMessage={fields.length === 0}
    />
  );
};
